// src/App.tsx
import React, { Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { DesktopEngagementActionsV2, MobileEngagementBar, ShareDialog, FeedbackDialog } from './components/engagement';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import { CompactHeader } from './components/layout/CompactHeader';
import { MobileBottomNav, MobileSearchOverlay } from './components/mobile';
import { useMobileFirst } from './hooks/useResponsive';
import { useCanonicalUrl } from './hooks/useDocumentTitle';
import { PeriodProvider } from './contexts/PeriodContext';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Lazy load pages for code splitting
const DeputyProfile = lazyWithRetry(() => import('./pages/DeputyProfileEnhanced'));
const Home = lazyWithRetry(() => import('./pages/HomeEnhanced'));
const Diputados = lazyWithRetry(() => import('./pages/DiputadosOptimized'));
const VotesPage = lazyWithRetry(() => import('./pages/VotesPage'));
const VoteDetail = lazyWithRetry(() => import('./pages/VoteDetail'));
const VoteComparison = lazyWithRetry(() => import('./pages/VoteComparison'));
const BulletinDetail = lazyWithRetry(() => import('./pages/BulletinDetail'));
const PartyAnalytics = lazyWithRetry(() => import('./pages/PartyAnalytics'));
const About = lazyWithRetry(() => import('./pages/About'));
const Apoya = lazyWithRetry(() => import('./pages/Apoya'));
const Transparencia = lazyWithRetry(() => import('./pages/Transparencia'));
const InstagramExplore = lazyWithRetry(() => import('./pages/InstagramExplore'));
const PromesasPage = lazyWithRetry(() => import('./pages/PromesasPage'));
const Metodologia = lazyWithRetry(() => import('./pages/Metodologia'));
const SemanaPage = lazyWithRetry(() => import('./pages/SemanaPage'));

// Payment pages
const PaymentSuccess = lazyWithRetry(() => import('./pages/PaymentSuccess'));
const PaymentFailure = lazyWithRetry(() => import('./pages/PaymentFailure'));
const PaymentPending = lazyWithRetry(() => import('./pages/PaymentPending'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));

// Chatbot page (disabled in production)
// Only check REACT_APP_ENVIRONMENT - NODE_ENV is always 'production' during CRA builds
const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
const Chatbot = !isProduction
  ? lazyWithRetry(() => import('./pages/Chatbot'))
  : null;

// Development only - Error boundary test page
const TestErrorBoundary = process.env.NODE_ENV === 'development'
  ? lazyWithRetry(() => import('./pages/TestErrorBoundary'))
  : null;

// Admin pages (password protected)
const AdminScraper = lazyWithRetry(() => import('./pages/AdminScraper'));

// Demo pages (disabled in production)
const PositionsDemo = !isProduction
  ? lazyWithRetry(() => import('./pages/PositionsDemo'))
  : null;
const ExtractionStatus = !isProduction
  ? lazyWithRetry(() => import('./pages/ExtractionStatus'))
  : null;

// Instagram content generator page (disabled in production)
const Instagram = !isProduction
  ? lazyWithRetry(() => import('./pages/Instagram'))
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});


// Mobile Header Component - Smart per-page header
const MobilePageHeader: React.FC<{ isHomePage: boolean; hasEnhancedHeader: boolean }> = ({ isHomePage, hasEnhancedHeader }) => {
  if (isHomePage) {
    // Home page: No header needed, hero section handles branding
    return null;
  }

  if (hasEnhancedHeader) {
    // Pages with EnhancedPageHeader: No additional header needed
    return null;
  }

  // Other pages: Clean header with context (fallback - should rarely be used now)
  return (
    <div className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="flex items-center justify-center h-12 px-4">
        <img
          src="/logo512.png"
          alt="Caramba"
          className="h-6 w-auto"
        />
      </div>
    </div>
  );
};

// Pie de página discreto: enlace a la metodología
const SiteFooter: React.FC = () => (
  <footer className="py-6 text-center text-xs text-gray-400">
    <Link to="/semana/" className="hover:text-gray-600 underline decoration-dotted underline-offset-2">
      Esta semana
    </Link>
    <span className="mx-2" aria-hidden="true">·</span>
    <Link to="/metodologia" className="hover:text-gray-600 underline decoration-dotted underline-offset-2">
      Metodología
    </Link>
    <span className="mx-2" aria-hidden="true">·</span>
    <Link to="/transparencia" className="hover:text-gray-600 underline decoration-dotted underline-offset-2">
      Transparencia
    </Link>
  </footer>
);

// Layout wrapper - Responsive mobile-first with desktop preservation
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showMobileUI, showDesktopUI } = useMobileFirst();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

  useCanonicalUrl(location.pathname);

  const isHomePage = location.pathname === '/';

  // Detect pages that use EnhancedPageHeader (most pages except home)
  const hasEnhancedHeader = !isHomePage && (
    location.pathname.startsWith('/diputados') ||
    location.pathname.startsWith('/votaciones') ||
    location.pathname.startsWith('/boletines/') ||
    location.pathname.startsWith('/partidos') ||
    location.pathname.startsWith('/deputy/') ||
    location.pathname.startsWith('/acerca') ||
    location.pathname.startsWith('/apoya') ||
    location.pathname.startsWith('/transparencia') ||
    location.pathname.startsWith('/promesas') ||
    location.pathname.startsWith('/semana') ||
    location.pathname.startsWith('/metodologia') ||
    location.pathname.startsWith('/chatbot')
  );

  // Show engagement actions on all pages
  const showEngagementActions = true;

  if (showMobileUI) {
    // Mobile-first UI: Page-aware header system
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Smart mobile header - only when needed */}
        <MobilePageHeader isHomePage={isHomePage} hasEnhancedHeader={hasEnhancedHeader} />

        {/* Main content area with proper spacing based on header presence */}
        <main className={
          isHomePage
            ? 'pb-20'
            : hasEnhancedHeader
              ? 'pb-20' // EnhancedPageHeader handles its own spacing
              : 'pt-12 pb-20' // Fallback for pages without EnhancedPageHeader
        }>
          <AnimatePresence mode="wait">
            {children}
          </AnimatePresence>
          <SiteFooter />
        </main>

        {/* Mobile engagement actions bar */}
        {showEngagementActions && (
          <MobileEngagementBar
            onSubscribe={() => navigate('/apoya')}
            onFeedback={() => setIsFeedbackDialogOpen(true)}
            onShare={() => setIsShareDialogOpen(true)}
            isSubscribed={false}
          />
        )}

        {/* Mobile bottom navigation */}
        <MobileBottomNav onSearchOpen={() => setIsSearchOpen(true)} />

        {/* Mobile search overlay */}
        <MobileSearchOverlay
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
        />

        {/* Share and Feedback Dialogs */}
        <ShareDialog
          isOpen={isShareDialogOpen}
          onClose={() => setIsShareDialogOpen(false)}
        />
        <FeedbackDialog
          isOpen={isFeedbackDialogOpen}
          onClose={() => setIsFeedbackDialogOpen(false)}
        />
      </div>
    );
  }

  // Desktop/Tablet UI with CompactHeader
  return (
    <div className="min-h-screen bg-gray-50">
      <CompactHeader />
      <main>
        <AnimatePresence mode="wait">
          {children}
        </AnimatePresence>
        <SiteFooter />
      </main>

      {/* Desktop engagement actions - fixed sidebar with integrated popovers */}
      {showEngagementActions && (
        <DesktopEngagementActionsV2
          onSubscribe={() => navigate('/apoya')}
          isSubscribed={false}
        />
      )}
    </div>
  );
};

// Wrapper for individual routes with error boundary
// /diputados/:id -> /deputy/:id
const DeputyRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/deputy/${id}`} replace />;
};

const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary level="page" showDetails={process.env.NODE_ENV === 'development'}>
      {children}
    </ErrorBoundary>
  );
};

// Main App Component
function App() {
  return (
    <ErrorBoundary level="app" showDetails={process.env.NODE_ENV === 'development'}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <PeriodProvider>
          <Layout>
            {/* Vercel Analytics & Speed Insights */}
            <Analytics />
            <SpeedInsights />
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={
                  <RouteErrorBoundary>
                    <Home />
                  </RouteErrorBoundary>
                } />
                <Route path="/desde-instagram" element={<RouteErrorBoundary><InstagramExplore /></RouteErrorBoundary>} />
                <Route path="/diputados" element={
                  <RouteErrorBoundary>
                    <Diputados />
                  </RouteErrorBoundary>
                } />
                <Route path="/votaciones" element={
                  <RouteErrorBoundary>
                    <VotesPage />
                  </RouteErrorBoundary>
                } />
                <Route path="/votaciones/comparar" element={
                  <RouteErrorBoundary>
                    <VoteComparison />
                  </RouteErrorBoundary>
                } />
                <Route path="/boletines/:boletin" element={
                  <RouteErrorBoundary>
                    <BulletinDetail />
                  </RouteErrorBoundary>
                } />
                <Route path="/votaciones/:voteId" element={
                  <RouteErrorBoundary>
                    <VoteDetail />
                  </RouteErrorBoundary>
                } />
                <Route path="/partidos" element={
                  <RouteErrorBoundary>
                    <PartyAnalytics />
                  </RouteErrorBoundary>
                } />
                <Route path="/deputy/:id" element={
                  <RouteErrorBoundary>
                    <DeputyProfile />
                  </RouteErrorBoundary>
                } />
                {/* Spanish alias of the profile route (the listing lives at /diputados) */}
                <Route path="/diputados/:id" element={<DeputyRedirect />} />
                <Route path="/acerca" element={
                  <RouteErrorBoundary>
                    <About />
                  </RouteErrorBoundary>
                } />
                <Route path="/apoya" element={
                  <RouteErrorBoundary>
                    <Apoya />
                  </RouteErrorBoundary>
                } />
                <Route path="/transparencia" element={
                  <RouteErrorBoundary>
                    <Transparencia />
                  </RouteErrorBoundary>
                } />
                <Route path="/semana" element={<RouteErrorBoundary><SemanaPage /></RouteErrorBoundary>} />
                <Route path="/semana/:semana" element={<RouteErrorBoundary><SemanaPage /></RouteErrorBoundary>} />
                <Route path="/promesas" element={
                  <RouteErrorBoundary>
                    <PromesasPage />
                  </RouteErrorBoundary>
                } />

                <Route path="/metodologia" element={
                  <RouteErrorBoundary>
                    <Metodologia />
                  </RouteErrorBoundary>
                } />

                {/* Chatbot route (feature flagged) */}
                {Chatbot && (
                  <Route path="/chatbot" element={
                    <RouteErrorBoundary>
                      <Chatbot />
                    </RouteErrorBoundary>
                  } />
                )}

                {/* Payment routes */}
                <Route path="/payment/success" element={
                  <RouteErrorBoundary>
                    <PaymentSuccess />
                  </RouteErrorBoundary>
                } />
                <Route path="/payment/failure" element={
                  <RouteErrorBoundary>
                    <PaymentFailure />
                  </RouteErrorBoundary>
                } />
                <Route path="/payment/pending" element={
                  <RouteErrorBoundary>
                    <PaymentPending />
                  </RouteErrorBoundary>
                } />

                {/* Redirect routes for cards that don't have dedicated pages yet */}
                <Route path="/comparar" element={<Navigate to="/diputados" replace />} />
                <Route path="/temas" element={<Navigate to="/votaciones?filter=temas" replace />} />
                <Route path="/efectividad" element={<Navigate to="/diputados?filter=efectividad" replace />} />

                {/* Development only route for testing error boundaries */}
                {process.env.NODE_ENV === 'development' && TestErrorBoundary && (
                  <Route path="/test-error" element={
                    <RouteErrorBoundary>
                      <TestErrorBoundary />
                    </RouteErrorBoundary>
                  } />
                )}

                {/* Admin routes (password protected) */}
                <Route path="/admin/scraper" element={
                  <RouteErrorBoundary>
                    <AdminScraper />
                  </RouteErrorBoundary>
                } />

                {/* Demo pages (feature flagged) */}
                {PositionsDemo && (
                  <Route path="/demo/posiciones" element={
                    <RouteErrorBoundary>
                      <PositionsDemo />
                    </RouteErrorBoundary>
                  } />
                )}
                {ExtractionStatus && (
                  <Route path="/demo/extraccion" element={
                    <RouteErrorBoundary>
                      <ExtractionStatus />
                    </RouteErrorBoundary>
                  } />
                )}
                {Instagram && (
                  <Route path="/instagram" element={
                    <RouteErrorBoundary>
                      <Instagram />
                    </RouteErrorBoundary>
                  } />
                )}

                {/* Catch-all: without it React Router renders nothing and the page stays blank */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Layout>
          </PeriodProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
